export type DurationParseResult =
  | {
      minutes: number;
    }
  | {
      error: true;
    };

export const getDecimalSeparator = (locale: string) => {
  const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
  return parts.find((part) => part.type === 'decimal')?.value ?? '.';
};

export const splitDurationMinutes = (totalMinutes: number) => {
  const minutes = Math.max(0, Math.round(totalMinutes));
  return {
    hours: Math.floor(minutes / 60),
    remainingMinutes: minutes % 60,
  };
};

export const formatDurationMinutes = (totalMinutes: number) => {
  const { hours, remainingMinutes } = splitDurationMinutes(totalMinutes);
  return `${hours}:${String(remainingMinutes).padStart(2, '0')}`;
};

export const getDurationInputSegment = (
  value: string,
  cursorPosition: number,
  locale: string,
): 'hours' | 'minutes' => {
  const colonIndex = value.indexOf(':');
  if (colonIndex !== -1) {
    return cursorPosition <= colonIndex ? 'hours' : 'minutes';
  }

  const decimalSeparator = getDecimalSeparator(locale);
  const decimalIndices = [value.indexOf('.'), value.indexOf(decimalSeparator)].filter(
    (index) => index !== -1,
  );
  if (decimalIndices.length > 0) {
    const decimalIndex = Math.min(...decimalIndices);
    return cursorPosition <= decimalIndex ? 'hours' : 'minutes';
  }

  return 'hours';
};

export const parseDurationInput = (value: string, locale: string): DurationParseResult | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { minutes: 0 };
  }

  if (trimmed.includes(':')) {
    const [hoursPart, minutesPart = '0'] = trimmed.split(':');
    if (!/^\d+$/.test(hoursPart) || !/^\d{1,2}$/.test(minutesPart)) {
      return { error: true };
    }

    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);
    if (minutes < 0 || minutes >= 60) {
      return { error: true };
    }

    return { minutes: Math.max(0, hours * 60 + minutes) };
  }

  const decimalSeparator = getDecimalSeparator(locale);
  const hasDecimalSeparator = trimmed.includes(decimalSeparator) || trimmed.includes('.');
  const normalized = trimmed.replace(',', '.').replace(decimalSeparator, '.');

  if (hasDecimalSeparator) {
    const hours = Number(normalized);
    if (!Number.isFinite(hours) || hours < 0) {
      return { error: true };
    }

    return { minutes: Math.round(hours * 60) };
  }

  if (!/^\d+$/.test(trimmed)) {
    return { error: true };
  }

  return { minutes: Number(trimmed) * 60 };
};
